import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { GetClientEntitiesByBillingGroupDocument } from "~/graphql/supergraphHooks";
import {
  type GetClientEntitiesByBillingGroupQuery,
  type ClientArenaFilter,
  type ClientArenaSort,
} from "~/graphql/supergraphTypes";
import type { EntityType as ClientEntityType } from "~/pages/Billing/ClientSpecificView/types";

type FilterInfo =
  | { filterType: "text"; filter?: string; values?: string[]; dateFrom?: string; type?: string }
  | {
      filterType: "number";
      filter?: number;
      values?: string[];
      dateFrom?: string;
      type?: string;
    }
  | { filterType: "set"; filter?: string; values?: string[]; dateFrom?: string; type?: string }
  | {
      filterType: "date";
      filter?: string;
      values?: string[];
      dateFrom?: string;
      type?: string;
    };

const isFilterInfo = (value: unknown): value is FilterInfo => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    obj.filterType === "text" ||
    obj.filterType === "set" ||
    obj.filterType === "date" ||
    obj.filterType === "number"
  );
};

export const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
  groupKeys: string[] = [],
): ClientArenaFilter[] | null => {
  if (!filterModel) {
    return null;
  }
  if ("filterType" in filterModel) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }

  const isLevel0 = groupKeys.length === 0;
  const queryFilters: ClientArenaFilter[] = [];

  for (const [field, filterInfo] of Object.entries(filterModel)) {
    if (
      isLevel0 &&
      field !== "billingGroupId" &&
      field !== "entity" &&
      field !== "jsqProductLine"
    ) {
      continue;
    }

    const mappedField = field === "billingGroupId" ? "billingGroupName" : field;

    if (!isFilterInfo(filterInfo)) {
      continue;
    }

    if (filterInfo.filterType === "text" && filterInfo.filter) {
      queryFilters.push({ field: mappedField, filterValues: [filterInfo.filter] });
    } else if (filterInfo.filterType === "number" && filterInfo.filter !== undefined) {
      queryFilters.push({ field: mappedField, filterValues: [String(filterInfo.filter)] });
    } else if (filterInfo.filterType === "set" && filterInfo.values) {
      queryFilters.push({ field: mappedField, filterValues: filterInfo.values });
    }
  }

  return queryFilters.length > 0 ? queryFilters : null;
};

export const getSortVars = (
  sortItems: AgGrid.SortModelItem[],
  groupKeys: string[] = [],
): ClientArenaSort[] | null => {
  if (!sortItems || sortItems.length === 0) {
    return null;
  }

  const isLevel0 = groupKeys.length === 0;
  const result: ClientArenaSort[] = [];

  for (const item of sortItems) {
    if (isLevel0 && item.colId !== "billingGroupId" && item.colId !== "activeInvestments") {
      continue;
    }
    result.push({
      field: item.colId === "billingGroupId" ? "billingGroupName" : item.colId,
      sortAscending: item.sort === "asc",
    });
  }

  return result;
};

export type BillingGroupEntityRow = {
  billingGroupId: string;
  billingGroupName: string | null;
  softwareEumUsd: number | null;
  billableFaEumUsd: number | null;
  activeInvestments?: number | null;
  childCount: number;
  type: "billingGroup" | "entity";
  entityId: number | null;
  entity: string | null;
  entityData: ClientEntityType | null;
};

type BillingGroupType = NonNullable<
  GetClientEntitiesByBillingGroupQuery["getClientEntitiesByBillingGroup"]["billingGroups"]
>[number];

export const rowsFromBillingGroups = (
  billingGroups: BillingGroupType[] | null | undefined,
): BillingGroupEntityRow[] => {
  if (!billingGroups) {
    return [];
  }
  return billingGroups.map((bg) => ({
    billingGroupId: bg.billingGroupId,
    billingGroupName: bg.billingGroupName,
    softwareEumUsd: bg.softwareEumUsd ?? null,
    billableFaEumUsd: bg.billableFaEumUsd ?? null,
    activeInvestments: bg.activeInvestments ?? null,
    childCount: bg.childCount,
    type: "billingGroup",
    entityId: null,
    entity: null,
    entityData: null,
  }));
};

export const rowsFromEntities = (
  entities: GetClientEntitiesByBillingGroupQuery["getClientEntitiesByBillingGroup"]["entities"],
  billingGroupId: string,
): BillingGroupEntityRow[] => {
  if (!entities) {
    return [];
  }
  return entities.map((entity) => ({
    billingGroupId,
    billingGroupName: null,
    softwareEumUsd: entity.currentBlendSoftwareEumUsd ?? null,
    billableFaEumUsd: entity.billableFaEumUsd ?? null,
    childCount: 0,
    type: "entity",
    entityId: entity.entityId,
    entity: entity.entity,
    entityData: entity,
  }));
};

export type EntitiesByBillingGroupDataSourceProps = {
  onTotalCountChange?: (count: number) => void;
  onFiltersChange?: (filters: ClientArenaFilter[] | null) => void;
  onSortsChange?: (sorts: ClientArenaSort[] | null) => void;
};

export class EntitiesByBillingGroupDataSource implements AgGrid.IServerSideDatasource {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  clientId: string;
  asOfDate: string;
  private onTotalCountChange?: EntitiesByBillingGroupDataSourceProps["onTotalCountChange"];
  private onFiltersChange?: EntitiesByBillingGroupDataSourceProps["onFiltersChange"];
  private onSortsChange?: EntitiesByBillingGroupDataSourceProps["onSortsChange"];

  constructor(
    clientId: string,
    props?: EntitiesByBillingGroupDataSourceProps,
    apolloClient: ApolloClient<NormalizedCacheObject> = client,
    asOfDate: string = "",
  ) {
    this.apolloClient = apolloClient;
    this.clientId = clientId;
    this.asOfDate = asOfDate;
    this.onTotalCountChange = props?.onTotalCountChange;
    this.onFiltersChange = props?.onFiltersChange;
    this.onSortsChange = props?.onSortsChange;
  }

  setAsOfDate(asOfDate: string): void {
    this.asOfDate = asOfDate;
  }

  public async getRows(
    params: AgGrid.IServerSideGetRowsParams<BillingGroupEntityRow>,
  ): Promise<void> {
    const groupKeys = params.request.groupKeys ?? [];
    const startRow = params.request.startRow ?? 0;
    if (groupKeys.length === 0 && startRow === 0) {
      params.api?.showLoadingOverlay();
    }
    const filters = getFilterVars(params.request.filterModel, groupKeys);
    const sorts = getSortVars(params.request.sortModel, groupKeys);
    const offset = params.request.startRow ?? 0;
    const calculatedLimit = (params.request.endRow ?? 0) - offset;
    const limit = calculatedLimit <= 0 ? 10 : calculatedLimit;

    this.onFiltersChange?.(filters);
    this.onSortsChange?.(sorts);

    try {
      const response = await this.apolloClient.query<GetClientEntitiesByBillingGroupQuery>({
        query: GetClientEntitiesByBillingGroupDocument,
        variables: {
          clientId: this.clientId,
          asOfDate: this.asOfDate,
          groupKeys,
          offset,
          limit,
          filters,
          sorts,
        },
        fetchPolicy: "no-cache",
      });

      const data = response.data.getClientEntitiesByBillingGroup;
      const totalCount = data.totalCount;
      const isLevel0 = groupKeys.length === 0;

      let rows: BillingGroupEntityRow[];
      if (isLevel0) {
        rows = rowsFromBillingGroups(data.billingGroups);
        this.onTotalCountChange?.(totalCount);
      } else {
        rows = rowsFromEntities(data.entities, groupKeys[0]);
      }

      const rowCount = isLevel0 ? totalCount : undefined;

      params.success({ rowData: rows, rowCount });

      if (isLevel0 && totalCount === 0) {
        params.api?.showNoRowsOverlay();
      } else if (isLevel0) {
        params.api?.hideOverlay();
      }
    } catch {
      params.fail();
    }
  }
}
